import { getFileSize } from 'app/utils/attachments';

import type { Channel } from '../richTextField.types';

import { validateWhatsAppAttachment } from './validateWhatsAppAttachment';

export const MAX_ATTACHMENTS_TOTAL_FILE_SIZE = 20 * 1024 * 1024;

/**
 * How many attachments a single message may carry, for the channels that limit
 * it, and what the agent is told when the limit is passed. Channels absent here
 * are only bound by the total size budget.
 */
const ATTACHMENT_COUNT_LIMITS: Partial<
  Record<Channel, { maxCount: number; message: string }>
> = {
  whatsapp: {
    maxCount: 1,
    message: 'WhatsApp messages can only include one attachment.',
  },
};

/** A file picked by the user that has not been validated yet. */
export type AttachmentCandidate = {
  file: File;
  uid: string;
};

/**
 * A file whose upload has started but has not landed in the form yet. Tracked
 * so that a second selection counts uploads that are still in flight.
 */
export type PendingFile = {
  name: string;
  size: number;
};

/**
 * A file already attached to the message. Only `size` is listed, because at
 * runtime these are the redux-form descriptors
 * (`{ name, absolute_uri, relative_uri, size, uid }`) rather than `File`s.
 */
export type AttachedFile = {
  size: number;
};

type ValidateAttachmentBatchOptions = {
  /** The files picked in a single interaction, in selection order. */
  candidates: AttachmentCandidate[];
  channel: Channel;
  attachedFiles: AttachedFile[];
  pendingFiles: PendingFile[];
};

const sumSizes = (files: Array<{ size: number }>): number =>
  files
    .map(({ size }) => size)
    .filter((size) => !isNaN(size))
    .reduce((sum, current) => sum + current, 0);

/**
 * Validates a whole selection of files against the limits of the message, and
 * returns the first violation found, worded for the user, or null when every
 * file may be uploaded.
 */
export const validateAttachmentBatch = ({
  candidates,
  channel,
  attachedFiles,
  pendingFiles,
}: ValidateAttachmentBatchOptions): string | null => {
  const alreadyCounted = attachedFiles.length + pendingFiles.length;
  const countLimit = ATTACHMENT_COUNT_LIMITS[channel];

  // Checked once for the whole selection rather than file by file, so that the
  // message does not depend on which file happens to trip a limit first.
  if (
    countLimit !== undefined &&
    alreadyCounted + candidates.length > countLimit.maxCount
  ) {
    return countLimit.message;
  }

  let totalSize = sumSizes(attachedFiles) + sumSizes(pendingFiles);

  for (const { file } of candidates) {
    if (channel === 'whatsapp') {
      const whatsAppRejection = validateWhatsAppAttachment({ file });

      if (whatsAppRejection) {
        return whatsAppRejection;
      }
    }

    if (totalSize + file.size > MAX_ATTACHMENTS_TOTAL_FILE_SIZE) {
      return `Attachments must stay under ${getFileSize(MAX_ATTACHMENTS_TOTAL_FILE_SIZE)} in total — this message would come to ${getFileSize(totalSize + file.size)}.`;
    }

    totalSize += file.size;
  }

  return null;
};
